import React from 'react';
import { Modal, Form, Col, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import BeSafeControlFeedback from 'components/commons/BeSafeControlFeedback';
import BeSafeSwitch from 'components/commons/BeSafeSwitch';
import NetworksSelect from 'components/configuration/firewall/modals/NetworksSelect';
import { Formik } from 'formik';
import {
  createAgentProfile,
  patchAgentProfile,
} from 'app/crud/agent_profiles.crud';
import ConfigurationSection from 'components/devices/configuration/common/ConfigurationSection';
import { successToast, errorToast } from 'utils/toastUtils';
import BeSafeButton from 'components/commons/BeSafeButton';
import { shallowEqual, useSelector } from 'react-redux';


const AddAgentProfileModal = ({
  editMode,
  selectedAgentProfile,
  showModal,
  onHide,
  onCreate,
  onUpdate,
}) => {
  const intl = useIntl();
  const { accountID: reduxAccountID } = useSelector(
    ({ auth }) => ({
      accountID: auth.user.account_id,
    }),
    shallowEqual
  );

  const initialNetworkValues = {
    name: editMode ? selectedAgentProfile?.name : '',
    antivirus: editMode
      ? selectedAgentProfile?.is_antivirus_host_checker_enabled
      : false,
    corporate_network: editMode
      ? selectedAgentProfile?.is_corporate_network_enabled
      : false,
    routing_address: editMode ? selectedAgentProfile?.network_objects : [],
  };

  return (
    <Modal show={showModal} onHide={onHide} centered size="md">
      <Modal.Header closeButton>
        <Modal.Title>
          {editMode ? (
            <FormattedMessage id="AGENTPROFILE.EDIT" />
          ) : (
            <FormattedMessage id="AGENTPROFILE.ADD" />
          )}
        </Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={{ ...initialNetworkValues }}
        onSubmit={(values, { setSubmitting }) => {

          setSubmitting(true);

          let params = {
            agent_profile: {
              name: values.name,
              is_antivirus_host_checker_enabled: values.antivirus,
              is_corporate_network_enabled: values.corporate_network,
              networks: values.routing_address,
            },
          };

          if (editMode) {
            patchAgentProfile(
              params,
              selectedAgentProfile.id,
              selectedAgentProfile.account_id
            )
              .then(() => {
                onUpdate(params.agent_profile);
                successToast({
                  body: 'AGENTPROFILE.EDIT_SUCCESS',
                  intl: intl,
                });
              })
              .catch((err) => {
                errorToast({ body: err.response.data?.error, intl: intl });
              })
              .finally(() => {
                setSubmitting(false);
              });
          } else {
            createAgentProfile(params, reduxAccountID)
              .then(() => {
                onCreate();
                successToast({
                  body: 'AGENTPROFILE.ADD_SUCCESS',
                  intl: intl,
                });
              })
              .catch((err) => {
                errorToast({ body: err.response.data?.error, intl: intl });
              })
              .finally(() => {
                setSubmitting(false);
              });
          }
        }}
        validate={(values) => {
          const errors = {};
          if (!values.name || !values.name.trim()) {
            errors.name = intl.formatMessage({ id: 'GENERAL.REQUIRED_FIELD' });
          }
          if (values.name.length > 64) {
            errors.name = intl.formatMessage({ id: 'GENERAL.MAX_NAME_SIZE' });
          }

          return errors;
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          handleBlur,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group controlId="name">
                    <Form.Label>
                      <FormattedMessage id="GENERAL.NAME" />
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      onChange={handleChange}
                      isInvalid={errors.name}
                      className="border-secondary"
                      placeholder={intl.formatMessage({
                        id: 'GENERAL.NAME_PLACEHOLDER',
                      })}
                      value={values.name}
                      required
                    />
                    {errors.name && touched.name && (
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <ConfigurationSection
                className="d-flex flex-column h-100"
                title={
                  <FormattedMessage id="AGENTPROFILE.HOST_CHECKER_SECTION" />
                }
                headerButtons={() => {}}
                handleSubmit={() => {}}
              >
                <Row>
                  <Col>
                    <Form.Group as={Row} className="align-items-center mb-3">
                      <Col md={4}>
                        <Form.Label className="mb-0">
                          <FormattedMessage id="AGENTPROFILE.ANTIVIRUS" />
                        </Form.Label>
                      </Col>
                      <Col md={8}>
                        <BeSafeSwitch
                          state={values.antivirus}
                          name="Antivirus"
                          onChange={(val) => {
                            setFieldValue('antivirus', val);
                          }}
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
              </ConfigurationSection>

              <ConfigurationSection
                className="d-flex flex-column h-100"
                title={
                  <FormattedMessage id="AGENTPROFILE.LOCATION_AWARENESS_SECTION" />
                }
                headerButtons={() => {}}
                handleSubmit={() => {}}
              >
                <Row>
                  <Col>
                    <Form.Group
                      as={Row}
                      controlId="corporate_network"
                      className="align-items-center mb-3"
                    >
                      <Col md={4}>
                        <Form.Label className="mb-0">
                          <FormattedMessage id="AGENTPROFILE.COPORATE_NETWORK" />
                        </Form.Label>
                      </Col>
                      <Col md={8}>
                        <BeSafeSwitch
                          state={values.corporate_network}
                          name="corporate_network"
                          onChange={(val) => {
                            setFieldValue('corporate_network', val);
                          }}
                        />
                      </Col>
                    </Form.Group>

                    {values.corporate_network && (
                      <Form.Group controlId="routing_address">
                        <Form.Label>
                          <FormattedMessage id="AGENTPROFILE.IP_CONFIG" />
                        </Form.Label>
                        <span>
                          <NetworksSelect
                            handleChange={(val) =>
                              setFieldValue(
                                'routing_address',
                                val.map((v) => ({
                                  ...v,
                                  data: {
                                    ...v.data,
                                    network_type: v.data.network_type,
                                  },
                                }))
                              )
                            }
                            selectType="source"
                            allowedNetworks={['network', 'network-group']}
                            initialTags={values.routing_address}
                            ipProtocol="ipv4"
                          />
                        </span>
                        <BeSafeControlFeedback>
                          {errors.routing_address}
                        </BeSafeControlFeedback>
                      </Form.Group>
                    )}
                  </Col>
                </Row>
              </ConfigurationSection>


            </Modal.Body>
            <Modal.Footer className="py-3">
              <BeSafeButton
                variant="primary"
                type="submit"
                disabled={isSubmitting}
                text={<FormattedMessage id="GENERAL.SAVE" />}
              />
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default AddAgentProfileModal;
